import React, { useEffect, useMemo, useState } from 'react';
import { Tree, theme, Spin } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import { CEControl } from 'store/ducks/Reporting/types';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import ApiService from 'utils/ApiCalls';
import * as Styled from '../../Layouts/Layout.styled';
import * as LocalStyled from 'pages/Reporting/ControlReport/ControlReport.styled';
import { buildOrgTree, buildTreeData } from './buildControlEffectivenessTree';
import { useTranslation } from 'react-i18next';

const { useToken } = theme;

const OrgHierarchyControlEffectivenessTree: React.FC = () => {
  const { token } = useToken();
  const { t } = useTranslation();
  const { scheme } = useSelector((state: RootState) => state.global);

  const [controls, setControls] = useState<CEControl[]>([]);
  const [loading, setLoading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    if (!shouldFetch) return;

    const fetchReport = async () => {
      setLoading(true);
      try {
        const response = await ApiService.getControlEffectivenessReport();
        if (
          response.data?.status === 'success' &&
          Array.isArray(response.data?.data)
        ) {
          setControls(response.data.data);
        } else {
          setControls([]);
        }
      } catch (error) {
        console.error(error);
        setControls([]);
      } finally {
        setLoading(false);
        setShouldFetch(false);
      }
    };

    fetchReport();
  }, [shouldFetch]);

  const treeData: DataNode[] = useMemo(() => {
    if (!controls.length) return [];
    return buildTreeData(buildOrgTree(controls), token);
  }, [controls, token]);

  return (
    <Styled.StyledLayout token={token}>
      <TitleBar
        title={t('CONTROL_EFFECTIVENESS')}
        goBackButtons={
          <GoBackButton to='/dependency_bpr' text={t('GO_BACK')} />
        }
      />
      <Styled.HorizontalLayoutMain theme={scheme}>
        <Styled.Container>
          <LocalStyled.HierarchyLevels>
            <Styled.StyledButton
              type='primary'
              onClick={() => setShouldFetch(true)}
              className='display-save-button'
            >
              {t('DISPLAY')}
            </Styled.StyledButton>
          </LocalStyled.HierarchyLevels>

          <Styled.ContainerDependency token={token}>
            <Styled.CardItemWrapper>
              {!loading && controls.length === 0 ? (
                <LocalStyled.EmptyState>
                  {t('common.noData')}
                </LocalStyled.EmptyState>
              ) : (
                <Spin spinning={loading}>
                  <Tree treeData={treeData} showLine />
                </Spin>
              )}
            </Styled.CardItemWrapper>
          </Styled.ContainerDependency>
        </Styled.Container>
      </Styled.HorizontalLayoutMain>
    </Styled.StyledLayout>
  );
};

export default OrgHierarchyControlEffectivenessTree;
